import { useMemo } from 'react';
import { ViewStyle } from 'react-native';
import { TextProps } from '@ui-kitten/components';
import lodashIsEmpty from 'lodash/isEmpty';

import CalendarIcon from '../../Assets/calendar.svg';
import ConferenceIcon from '../../Assets/conference-room.svg';
import DiningIcon from '../../Assets/diningIcon.svg';
import MoneyIcon from '../../Assets/money.svg';
import { getEventPriceRange, getTablePriceRange } from '../../Helper';
import DateHelper from '../../Helper/Date';
import { EventDetails } from '../../RTK/api/events/types';
import { TableDetails } from '../../RTK/api/reservations/types';

import ListItem from '../ListItem';
import ResponsiveText from '../ResponsiveText';

type Data =
  | {
      eventData?: never;
      tableData: Pick<
        TableDetails,
        'is_communal' | 'experiences' | 'maximum' | 'minimum'
      >;
    }
  | {
      tableData?: never;
      eventData: Pick<
        EventDetails,
        'available_days' | 'end_date' | 'tickets' | 'time_slots' | 'start_date'
      >;
    };

type Props = Data & {
  contentContainerStyle?: ViewStyle;
  iconSize?: number;
  textCategory: TextProps['category'];
};

const Overview = ({
  contentContainerStyle,
  textCategory,
  iconSize = 24,
  tableData,
  eventData,
}: Props) => {
  const data = useMemo(() => {
    const isEvent = !lodashIsEmpty(eventData);
    if (isEvent) {
      const { isLastStart, startFormatted, start } =
        DateHelper.getEventScheduleInfo(
          eventData.start_date,
          eventData.end_date,
          eventData.time_slots,
          eventData.available_days,
        );
      const prices = getEventPriceRange(eventData.tickets);
      return [
        {
          leftIcon: { svg: CalendarIcon, size: iconSize },
          text: isLastStart
            ? startFormatted
            : DateHelper.formatDateWithText(
                start,
                eventData.end_date,
                eventData.available_days,
              ),
        },
        {
          leftIcon: { name: 'clock-outline', size: iconSize },
          text: DateHelper.formatTimeRangeArray(eventData.time_slots),
        },
        {
          leftIcon: { svg: MoneyIcon, size: iconSize },
          text: `${prices} per ticket`,
        },
      ];
    } else {
      const initialData = [
        {
          leftIcon: { svg: DiningIcon, size: iconSize },
          text: `Reservation for Parties ${tableData.minimum} to ${tableData.maximum}`,
        },
        {
          leftIcon: { svg: MoneyIcon, size: iconSize },
          text: `Reservation Fee: ${getTablePriceRange(tableData.experiences)}`,
        },
      ];
      if (tableData.is_communal) {
        initialData.push({
          leftIcon: { svg: ConferenceIcon, size: iconSize },
          text: 'Communal',
        });
      }
      return initialData;
    }
  }, [eventData, iconSize, tableData]);

  return data.map((item) => (
    <ListItem
      key={item.text}
      contentContainerStyle={contentContainerStyle}
      leftIcon={item.leftIcon}
      title={
        <ResponsiveText category={textCategory} numberOfLines={1}>
          {item.text}
        </ResponsiveText>
      }
      spacing={5}
    />
  ));
};

export default Overview;
